import * as Num from '@pkg/utils/numbers';
import config from '@/lib/config';
import { ActivityState, DesignEntity, Visibility } from '@/lib/enums';
import applyFilter from '../filters/apply';
import extendFilterConditions from '../filters/extendFilterConditions';
import make from '../make';
import defaultSnapshotMetrics from '../utils/defaultSnapshotMetrics';
import deriveHierarchy from '../utils/deriveHierarchy';
import deriveEntities from './deriveEntities';
import roundFloatMetrics from './shared/roundFloatMetrics';
import spanMetrics from './spanMetrics';

/**
 * @param {Object} input
 * @param {Object} input.snapshot
 * @param {Object[]} [input.filterConditions]
 * @param {Object} [input.library]
 * @param {Map} [input.skillMap]
 * @param {Boolean} [input.includeHierarchy]
 * @param {Boolean} [input.includeMetrics]
 * @param {Boolean} [input.includeTagMetrics]
 * @return {Object}
 *
 */
export default function deriveSnapshot({
  filterConditions,
  forceActivityState,
  includeHierarchy = true,
  includeMetrics = true,
  includeTagMetrics = true,
  library,
  resetFilters,
  settings,
  skillMap,
  snapshot,
}) {
  // setup
  console.groupCollapsed('Snapshots.deriveProps');
  console.groupCollapsed('Snapshots.deriveProps.trace');
  console.trace('trace');
  console.groupEnd('Snapshots.deriveProps.trace');
  console.time('Snapshots.deriveProps');

  // construct make whitelist
  const whitelist = new Map();
  whitelist.set('roles', ['__layer']);
  if (!includeHierarchy) {
    Object.entries(config.HIERARCHY_PROPERTIES).forEach(([key, value]) => {
      whitelist.set(key, value);
    });
  }

  // copy the snapshot
  console.time('Snapshots.deriveProps.make');
  const derived = make(snapshot, false, whitelist);
  console.timeEnd('Snapshots.deriveProps.make');

  // determine activity state
  const activityState =
    forceActivityState || settings?.activities?.state || ActivityState.ACTIVE;

  // pluck all entity keys
  derived.__keys = {};
  for (const plural in derived.entities) {
    derived.__keys[plural] = new Set(Object.keys(derived.entities[plural]));
  }

  // derive hierarchy
  if (includeHierarchy) {
    console.time('Snapshots.deriveProps.utils.getHierarchy');
    deriveHierarchy({ snapshot: derived, activityState });
    console.timeEnd('Snapshots.deriveProps.utils.getHierarchy');
  } else {
    console.debug('Snapshots.deriveProps.utils.getHierarchy: [skipped]');
  }

  // extend and apply filter conditions
  const filters = extendFilterConditions(
    activityState,
    filterConditions,
    resetFilters
  );

  if (filters.empty && !resetFilters) {
    console.debug('Snapshots.deriveProps.filter: [skipped]');
  } else {
    console.time('Snapshots.deriveProps.filter');
    applyFilter(derived, filters.conditions, skillMap, resetFilters);
    console.timeEnd('Snapshots.deriveProps.filter');
  }

  // calculate metrics
  if (includeMetrics && library) {
    const metrics = new Map();
    metrics.set('*', defaultSnapshotMetrics());

    console.time('Snapshots.deriveProps.entities.derive.metrics');
    deriveEntities({
      activityState,
      includeTagMetrics,
      metrics,
      library,
      snapshot: derived,
    });
    console.timeEnd('Snapshots.deriveProps.entities.derive.metrics');

    console.time('Snapshots.deriveProps.entities.derive.spans');
    spanMetrics(derived);
    console.timeEnd('Snapshots.deriveProps.entities.derive.spans');

    // finalize
    console.time('Snapshots.deriveProps.totals');
    const root = roundFloatMetrics(metrics.get('*'), false);
    derived.__metrics = root;
    derived.__percentage = null;
    derived.__type = DesignEntity.ORGANISATION;
    derived.__visibility = derived.__visibility ?? Visibility.FULL;

    if (Number.isFinite(root.total.hours)) {
      derived.__percentage = Num.percent(root.visible.hours, root.total.hours);
    }

    /** @deprecated */
    derived.__total_metrics = root.total;
    derived.__visible_metrics = root.visible;
    console.timeEnd('Snapshots.deriveProps.totals');
  } else {
    console.debug('Snapshots.deriveProps.entities.derive.metrics: [skipped]');
    console.debug('Snapshots.deriveProps.entities.derive.spans: [skipped]');
    console.debug('Snapshots.deriveProps.totals: [skipped]');
  }

  console.timeEnd('Snapshots.deriveProps');
  console.groupEnd('Snapshots.deriveProps');
  return Object.freeze(derived);
}
